import type React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';

import deepEqual from 'fast-deep-equal';
import type { History } from 'history';
import { iframeResizer } from 'iframe-resizer';
import { ReactRelayContext } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import { useCallbackOne } from 'use-memo-one';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getGlobalTheme } from '@atlaskit/tokens/get-global-theme';
import type { ThemeState } from '@atlaskit/tokens/theme-state';
import type { ProductEnvironment } from '@atlassian/forge-ui-types/environments';
import type { ForgeUIExtensionType, ContextId } from '@atlassian/forge-ui-types/extension';
import type { ForgeDoc } from '@atlassian/forge-ui-types/forge';
import type { ExtensionPayload, ExtensionData } from '@atlassian/forge-ui-types/public/effect';

import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import {
	type FetchUserRecommendationsPayload,
	type FetchUserRecommendationsResponse,
} from '../../bridge/methods/fetchUserRecommendations';
import {
	type RequestTeamworkGraphPayload,
	type RequestTeamworkGraphResponse,
} from '../../bridge/methods/requestTeamworkGraph';
import { useMetricsContext } from '../../context';
import { captureAndReportError, useTracingContext } from '../../error-reporting';
import { getPerformanceMark } from '../../performance-profiling';
import { usePerformanceAnalytics } from '../../performance-profiling/usePerformanceAnalytics';
import type { EmbeddedMacroParentType } from '../../renderer/forge-ui-renderer/types';
import { isCustomUI, parseExtensionId } from '../../utils';
import { isBodiedLayout } from '../../utils/getExtensionType';
import {
	type PublishRealtimeChannel,
	type SubscribeRealtimeChannel,
	type EmitPublicEvent,
} from '../../web-client';
import type { GQLForgeRateLimitProperties } from '../../web-client/graphql/types';
import type { FrontendLogEntry } from '../../web-client/queries/emitFrontendLogs';
import { getIsRovoEnabled } from '../../web-client/queries/getIsRovoEnabled';
import type { ViewContext, HTMLCustomIFrameElement } from '../iframe/types';
import { isInlineMacroPresentation } from '../iframe/utils/isInlineMacroPresentation';
import { withMetricsAndAnalytics } from '../metrics';
import { emit, on, onPublic, type Subscription, type ExtensionDetails } from './methods/events';
import {
	getUseBridgePermissionFunctions,
	type PermissionBridgeMethods,
} from './methods/permissions/usePermission';
import { reload } from './methods/reload';
import { type BridgeNavigatePayload, type FlagOptions, type FlagAction } from './types';
import { createNavigateTargetPath } from './utils/createNavigateTargetPath';
import { createMessageForwarder } from './utils/embedded-macros/createMessageForwarder';
import { transformDataForEmbeddedMacros } from './utils/embedded-macros/helpers';
import type { BridgeDataToForward, FAUSHandledBridgeMethods } from './utils/embedded-macros/types';
import { isCustomDomain } from './utils/isCustomDomain';
import { useGetAppForgeUIExtensions } from './utils/useGetAppForgeUIExtensions';

let storedBaseUrlRelay = '';
let createIframeBridge: any = null;
let postRobotSend: any = null;
// bridge-core import relies on window object. It is not available in SSR.
if (typeof window !== 'undefined') {
	createIframeBridge = require('@atlassian/bridge-core').createIframeBridge;
	postRobotSend = require('@atlassian/bridge-core').postRobotSend;
}

// Type of these methods could be shared with @forge/bridge
type NavigateType = 'same-tab' | 'new-tab';

type TargetNavigatePayload = {
	type: NavigateType;
} & Parameters<typeof createNavigateTargetPath>[0];

export type NavigatePayload = BridgeNavigatePayload;

type BaseOpenRovoPayload = {
	prompt?: string;
	// Used when forwarding from embedded macros to pass the source app's ID
	sourceAppId?: string;
	sourceEnvironmentId?: string;
	sourceInstallationId?: string;
};

type ForgeAgentPayload = BaseOpenRovoPayload & {
	agentKey: string;
	agentName: string;
};

type NonForgeAgentPayload = BaseOpenRovoPayload & {
	agentKey?: never;
	agentName?: string;
};

export type OpenRovoPayload = ForgeAgentPayload | NonForgeAgentPayload;
interface FetchProductPayload {
	fetchRequestInit: Omit<RequestInit, 'body'> & { body?: string | null };
	isMultipartFormData: boolean;
	product: 'jira' | 'confluence';
	restPath: string;
}

interface FetchRemotePayload {
	fetchRequestInit: Omit<RequestInit, 'body'> & { body?: string | null; path?: string };
	isMultipartFormData: boolean;
	remoteKey: string;
}
interface BaseFetchResponse {
	body?: string;
	headers: { [key: string]: string };
	isAttachment?: boolean;
	status: number;
	statusText: string;
}

type FetchRemoteResponse = BaseFetchResponse;
type FetchProductResponse = BaseFetchResponse;

export type ApiRefType = {
	changeWindowTitle: (title: string) => void;
	close: (payload?: any) => void;
	closeFlag: (options: Pick<FlagOptions, 'id'>) => Promise<boolean | void>;
	createHistory: () => History<any> | undefined;
	emitPublic?: EmitPublicEvent;
	emitReadyEvent: () => void;
	fetchProduct: (payload: FetchProductPayload) => Promise<FetchProductResponse>;
	fetchRemote: (payload: FetchRemotePayload) => Promise<FetchRemoteResponse>;
	fetchUserRecommendations?: (
		payload: FetchUserRecommendationsPayload,
	) => Promise<FetchUserRecommendationsResponse>;
	getContext: () => Promise<ViewContext>;
	getFrameDispatch: () => Promise<((action?: unknown) => void) | undefined>;
	getFrameId: () => string | null;
	onClose: (payload: () => void) => void;
	// Optional: `useBridge` is a public subpath export, so making this required would be a breaking
	// change for external consumers that construct an `ApiRefType`. The handler below no-ops when a
	// consumer hasn't supplied it.
	onEmitFrontendCustomMetric?: (data: { customMetricName: string; value: number }) => Promise<void>;
	onEmitFrontendLogs?: (data: { logs: FrontendLogEntry[] }) => Promise<void>;
	onError: (options: { error: Error }) => Promise<boolean | void>;
	onInitFeatureFlags: (data: any) => Promise<any>;
	onInvoke: (payload: { functionKey: string; metadata?: object; payload: object }) => Promise<
		| {
				metadata?: { rateLimitProperties?: GQLForgeRateLimitProperties | null };
				response: object;
				type: 'ok';
		  }
		| { authUrl: string; type: '3lo' }
		| { message: string; type: 'err' }
	>;
	onNavigate: (payload: NavigatePayload) => Promise<void>;
	onThreeLO: (authUrl: string, retry: () => Promise<void>) => Promise<void>;
	open: () => void;
	openModal: (opts: any) => void;
	openRovo?: (payload: OpenRovoPayload) => Promise<boolean | void>;
	publishRealtimeChannel?: PublishRealtimeChannel;
	reconcile: (options: { forgeDoc: ForgeDoc }) => Promise<boolean | void>;
	refresh: () => Promise<boolean | void>;
	requestTeamworkGraph?: (
		payload: RequestTeamworkGraphPayload,
	) => Promise<RequestTeamworkGraphResponse>;
	showFlag: (options: FlagOptions) => Promise<boolean | void>;
	submit: (payload?: any) => void;
	subscribeRealtimeChannel?: SubscribeRealtimeChannel;
} & PermissionBridgeMethods;

interface BridgeProvider {
	api: ApiRefType;
	cloudId?: string;
	contextIds: ContextId[];
	customBridgeMethods?: Record<string, (...args: any[]) => any>;
	embeddedMacroParentType?: EmbeddedMacroParentType;
	embeddedParentLocalId?: string;
	environment: ProductEnvironment;
	extension: ForgeUIExtensionType;
	extensionData?: ExtensionData;
	extensionPayload?: ExtensionPayload;
	height?: string;
	iframeRef: React.RefObject<HTMLCustomIFrameElement>;
	isEmbeddedMacro?: boolean;
	isInModal?: boolean;
	isResizable?: boolean;
	localId?: string;
	onLoad?: () => void;
	origin: string;
	surfaceColor?: string | null;
	theme?: Partial<ThemeState> | null;
}

type Result = {
	iframeProps: React.DetailedHTMLProps<
		React.IframeHTMLAttributes<HTMLIFrameElement>,
		HTMLIFrameElement
	>;
	loading: boolean;
};

const mapCustomBridgeMethods = ({
	customBridgeMethods,
	withFausMessageForwarder,
	isUIKitEmbeddedMacro,
	embeddedMacroLocalId,
}: {
	customBridgeMethods?: Record<string, (...args: any[]) => any>;
	embeddedMacroLocalId?: string;
	isUIKitEmbeddedMacro?: boolean;
	withFausMessageForwarder: any;
}) => {
	if (!customBridgeMethods) {
		return {};
	}

	return Object.keys(customBridgeMethods).reduce((acc, key) => {
		return {
			...acc,
			[key]: withFausMessageForwarder(key, (payload: any) => {
				const data = isUIKitEmbeddedMacro
					? transformDataForEmbeddedMacros(key, embeddedMacroLocalId, payload?.data)
					: payload?.data;
				return customBridgeMethods[key](data);
			}),
		};
	}, {});
};

/**
 * BridgeClientError represents an error that will reject to the user.
 * It is a "normal" error that should not be included in our error metrics/tracking.
 */
export class BridgeClientError extends Error {
	constructor(cause: string) {
		super(cause);
	}
}

const MACRO_MODULE_NAME = 'xen:macro';

/** Page name when rendered by FAUS (Forge App UI Service). Used to decide if bridge messages should be forwarded to the product. */
export const FAUS_PAGE_NAME = 'forge-adf-renderer';

// Tracks re-initialisation of the bridge and logs a warning to console and analytics
const useBridgeReinitTracker = () => {
	const { trackExtensionRemounted } = useForgeUiAnalyticsEvent();
	const mountedCount = useRef(0);
	const trackBridgeReinited = useCallbackOne(() => {
		try {
			mountedCount.current += 1;
			if (mountedCount.current > 1) {
				trackExtensionRemounted({
					mountCount: mountedCount.current,
					isUseBridgeReinit: true,
				});
				// eslint-disable-next-line no-console
				console.warn(`Warning: useBridge re-initialised the bridge ${mountedCount.current} times.`);
			}
		} catch {}
	}, [mountedCount, trackExtensionRemounted]);

	return trackBridgeReinited;
};

export const useBridge = ({
	cloudId,
	theme,
	surfaceColor,
	origin,
	api,
	customBridgeMethods,
	onLoad,
	isResizable,
	height,
	environment,
	extension,
	iframeRef,
	contextIds,
	isInModal,
	localId,
	isEmbeddedMacro = false,
	embeddedMacroParentType,
	embeddedParentLocalId,
	extensionPayload,
	extensionData,
}: BridgeProvider): Result => {
	const [loading, setLoading] = useState(true);
	const [loadedAt, setLoadedAt] = useState<number | null>(null);
	const { page } = useMetricsContext();
	// This ref avoids the api object ending up in the dep array
	// of the effect that calls createIframeBridge. It means that
	// people can pass anonymous functions as the api without the
	// bridge being re-created every render.
	const apiRef = useRef(api);
	useEffect(() => {
		apiRef.current = api;
	}, [api]);

	// Used for passing embedded macro extension object to
	// parent bodied macro Custom UI renderer.
	const extensionRef = useRef(extension);
	// Used for passing embedded macro extensionPayload to  modal renderer.
	// Only used in editor view mode so config will always be up to date.
	const extensionPayloadRef = useRef(extensionPayload);
	// Used for passing embedded macro extensionData to modal renderer
	// Extension data is a static object containing context values for the app, so no update needed.
	const extensionDataRef = useRef(extensionData);

	const enableThemingRef = useRef<CallableFunction>();
	const setThemeRef = useRef<CallableFunction>();

	// this ref tracks the initial theme to avoid unnecessary theme updates.
	// specifically, it is used in createIframeBridge during the initial forge runtime initialization,
	// so that the iframe can set the theme correctly on the ready event (initial handshake).
	// however, subsequent theme should not cause createIframeBridge to be called again.
	const initialThemeRef = useRef<{
		surfaceColor: string | null | undefined;
		theme: Partial<ThemeState> | null | undefined;
	}>({
		theme,
		surfaceColor,
	});
	const trackBridgeReinited = useBridgeReinitTracker();

	useEffect(() => {
		if (!setThemeRef.current) {
			return;
		}

		// we track the theme changes here to avoid calling createIframeBridge (expensive and error prone) unnecessarily
		initialThemeRef.current = {
			theme,
			surfaceColor,
		};
		setThemeRef.current(theme, surfaceColor, environment);
		emit('FORGE_CORE_THEME_CHANGED', { theme });
	}, [theme, surfaceColor, loading, environment]);

	// same as above, but for customBridgeMethods
	const customBridgeMethodsRef = useRef(customBridgeMethods);
	useEffect(() => {
		customBridgeMethodsRef.current = customBridgeMethods;
	}, [customBridgeMethods]);

	const [errorExtensionDetails, setErrorExtensionDetails] = useState({
		id: extension.id,
		properties: extension.properties,
		type: extension.type,
		environmentType: extension.environmentType,
		appOwnerAccountId: extension.appOwner?.accountId,
	});

	const tracing = useTracingContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { trackObjectStoreAction, trackHandShakeMetric } = useForgeUiAnalyticsEvent();

	useEffect(() => {
		const { type, environmentType, properties, appOwnerAccountId, id } = errorExtensionDetails;

		if (
			extension.id !== id ||
			extension.type !== type ||
			extension.environmentType !== environmentType ||
			extension.appOwner?.accountId !== appOwnerAccountId ||
			!deepEqual(extension.properties, properties)
		) {
			setErrorExtensionDetails({
				id: extension.id,
				properties: extension.properties,
				type: extension.type,
				environmentType: extension.environmentType,
				appOwnerAccountId: extension.appOwner?.accountId,
			});
		}
	}, [extension, errorExtensionDetails]);

	const { id, installationId } = extension;

	// Need to retrieve appId here because extension.appId could be undefined in certain products. For example, Jira extensions do not provide the appId.
	const appId = extension.appId ?? parseExtensionId(extension.id).appId;
	const getAppForgeUIExtensions = useGetAppForgeUIExtensions({
		appId: appId,
		environmentId: extension.environmentId,
		moduleType: extension.type,
		contextIds,
		context: {},
		includeHidden: false,
		cloudId,
		bypassNewForgeExtensionListCall: true,
	});

	const relayContext = useContext(ReactRelayContext);
	const relayEnvironment = relayContext?.environment;

	// Query to retrieve the base url of a tenant.
	// The same Atlassian tenant could have a different custom domain for each product.
	// We retrieve the underlying Atlassian domain here to use for URL creation in routing to support cross custom domain routing
	const getBaseUrl = useCallback(async () => {
		if (!cloudId) {
			return window.location.origin;
		}

		if (relayEnvironment) {
			if (storedBaseUrlRelay) {
				return storedBaseUrlRelay;
			}
			const observable = fetchQuery(
				relayEnvironment,
				graphql`
					query useBridge_tenantUserQuery($cloudIds: [ID!]) {
						tenantContexts(cloudIds: $cloudIds) {
							cloudUrl
						}
					}
				`,
				{
					cloudIds: [cloudId],
				},
			);
			return new Promise<string>((resolve, reject) => {
				let settled = false;
				observable.subscribe({
					next: (data) => {
						if (settled) {
							return;
						}
						settled = true;
						const baseUrl =
							(data as { tenantContexts?: Array<{ cloudUrl: string }> } | null)?.tenantContexts?.[0]
								?.cloudUrl ?? '';
						storedBaseUrlRelay = baseUrl;
						resolve(baseUrl);
					},
					error: (err: any) => {
						if (!settled) {
							settled = true;
							reject(err);
						}
					},
					complete: () => {
						if (!settled) {
							settled = true;
							resolve(storedBaseUrlRelay ?? '');
						}
					},
				});
			});
		} else {
			return window.location.origin;
		}
	}, [cloudId, relayEnvironment]);

	const appType = useMemo(() => (isCustomUI(extension) ? 'CustomUI' : 'UIKit'), [extension]);

	const { captureEvent, captureUntrustedEvent } = usePerformanceAnalytics();

	// The event subscription need to be maintained outside of useEffect to avoid missing events when the createIframeBridge is re-created
	// specifically, createIframeBridge could be re-created when the app "re-render", however, in such cases we do not want to lose the existing event subscriptions
	// after all, we should consider ensure createIframeBridge not to be re-created unnecessarily (only when completely app re-mount is really needed)
	const eventCallbacksRef = useRef<Subscription[]>([]);
	useEffect(() => {
		return () => {
			for (const subscription of eventCallbacksRef.current) {
				subscription.unsubscribe();
			}
			eventCallbacksRef.current = [];
		};
	}, []);

	const {
		properties: { layout },
		type,
		environmentType,
	} = extension;

	useEffect(() => {
		//this should never happen on client side
		if (!createIframeBridge) {
			return;
		}
		const iframeEl = iframeRef.current;

		if (iframeEl?.contentWindow && !loading && typeof loadedAt === 'number') {
			const extensionDetails = {
				appId,
				id: id.split('/').slice(0, 3).join('/'),
				installationId,
			};

			const sendHandshakeMetric = (page: string): ((success: boolean, error?: Error) => void) => {
				return (success: boolean, error?: Error): void => {
					trackHandShakeMetric({
						source: page,
						success,
						error,
					});

					if (error) {
						const handshakeError =
							error instanceof Error
								? new Error(`Bridge handshake error: ${error.message}`)
								: new Error('Unknown bridge handshake error');

						captureAndReportError({
							error: handshakeError,
							environment,
							errorExtensionDetails,
							page,
							tracing,
						});
					}
				};
			};

			// Only forward when we're the gateway (FAUS). Product (e.g. forge-ui-kit adf-renderer) uses bridge directly.
			// TODO: isEmbeddedMacro prop can be removed after rollout of platform_forge_ui_refactor_macro_parent_type
			const isRenderedInFAUS = page === FAUS_PAGE_NAME;

			const embeddedMacroAdditionalForwardedData: BridgeDataToForward = {
				sourceAppId: appId,
				sourceInstallationId: extensionRef.current.installationId,
				sourceEnvironmentId: extensionRef.current.environmentId,
				extension: extensionRef.current,
				isEmbeddedMacroModal: true,
				extensionPayload: extensionPayloadRef.current,
				extensionData: extensionDataRef.current,
				embeddedMacroExtensionDetails: extensionDetails,
				embeddedMacroLocalId: localId,
			};

			const withFausMessageForwarder = (
				methodName: FAUSHandledBridgeMethods,
				bridgeMethodFn: any,
			) => {
				// Only forward when we're the gateway (FAUS). Product (e.g. forge-ui-kit adf-renderer) uses bridge directly.
				const isRenderedInFAUS = page === FAUS_PAGE_NAME;
				const shouldUseMessageForwarder = fg('platform_forge_ui_refactor_macro_parent_type')
					? embeddedMacroParentType === 'CustomUI'
					: isEmbeddedMacro && isRenderedInFAUS;

				return shouldUseMessageForwarder
					? createMessageForwarder(
							methodName,
							embeddedMacroAdditionalForwardedData,
							postRobotSend,
							embeddedParentLocalId,
							extensionDetails,
						)
					: bridgeMethodFn;
			};

			// Only bodied macros should receive embedded macro messages
			const isBodiedMacro = isBodiedLayout(layout);
			// Confluence embed card has the same origin as top level product
			const isNativeConfluenceEmbeddedCard = () => {
				try {
					window.parent.location.href;
					return true;
				} catch {
					return false;
				}
			};

			const isProductLevel =
				typeof window !== 'undefined' &&
				(window.location.ancestorOrigins?.length === 0 || isNativeConfluenceEmbeddedCard()) &&
				!isInModal;

			const { open } = createIframeBridge({
				theme: initialThemeRef.current.theme,
				surfaceColor: initialThemeRef.current.surfaceColor,
				withDomain: origin,
				withWindow: iframeEl.contentWindow,
				metadata: {
					env: environment,
					module: type!,
					product: type!.split(':')[0],
					isFedRamp: isFedRamp(),
					localId,
				},
				features: withMetricsAndAnalytics({
					features: {
						invoke: async ({ data }) => {
							const performanceStart = getPerformanceMark();
							captureEvent({
								type: 'resolver-invoke-initiated',
								elapsed: performanceStart,
							});

							const { onInvoke, onThreeLO } = apiRef.current;
							const result = await onInvoke(data);

							if (result.type === '3lo') {
								return new Promise((resolve, reject) => {
									onThreeLO(result.authUrl, () => {
										const performanceAuthEnd = getPerformanceMark();
										return onInvoke(data).then((res) => {
											const performanceEnd = getPerformanceMark();
											captureEvent({
												type: 'resolver-invoked',
												duration: performanceEnd - performanceStart,
												authDuration: performanceAuthEnd - performanceStart,
												elapsed: performanceEnd,
											});

											if (res.type === 'ok') {
												if (!!data.metadata && fg('forge-ui-return-invocation-metadata')) {
													const optsInToRateLimitProperties =
														data.metadata?.rateLimitProperties === true;
													resolve({
														body: res.response,
														metadata: {
															...(optsInToRateLimitProperties
																? {
																		rateLimitProperties: res.metadata?.rateLimitProperties ?? null,
																	}
																: {}),
														},
													});
												} else {
													resolve(res.response);
												}
											}

											// Unexpected case where 3LO consent requested again after consenting
											reject();
										}, reject);
									});
								});
							}

							const performanceEnd = getPerformanceMark();
							captureEvent({
								type: 'resolver-invoked',
								duration: performanceEnd - performanceStart,
								authDuration: 0,
								elapsed: performanceEnd,
							});

							if (result.type === 'err') {
								throw new Error(result.message);
							}

							if (!!data.metadata && fg('forge-ui-return-invocation-metadata')) {
								const optsInToRateLimitProperties = data.metadata?.rateLimitProperties === true;
								return {
									body: result.response,
									metadata: {
										...(optsInToRateLimitProperties
											? {
													rateLimitProperties: result.metadata?.rateLimitProperties ?? null,
												}
											: {}),
									},
								};
							}
							return result.response;
						},
						initFeatureFlags: async ({ data }) => {
							const { onInitFeatureFlags } = apiRef.current;
							const result = await onInitFeatureFlags(data);

							if (result.type === 'err') {
								return {};
							}

							return result.response;
						},
						emitFrontendCustomMetric: async ({ data }) => {
							if (!fg('platform_forge_frontend_custom_metrics')) {
								return;
							}

							try {
								await apiRef.current.onEmitFrontendCustomMetric?.(data);
							} catch {
								// Silent fail: emitting a metric must never surface an error to the app.
							}
						},
						emitFrontendLogs: async ({ data }) => {
							if (!fg('platform_forge_frontend_logs')) {
								return;
							}

							try {
								await apiRef.current.onEmitFrontendLogs?.(data);
							} catch {
								// Silent fail: emitting logs must never surface an error to the app.
							}
						},
						// No-op: kept so existing apps calling this bridge method don't error.
						trackFeatureFlagEvent: async () => {},
						'iframe-analytics': async ({ data }) => {
							captureUntrustedEvent(data);
						},
						'ufo-event': async ({ data }: { data: unknown }) => {
							captureUntrustedEvent(data);
						},
						getUrl: async ({ data }: { data: TargetNavigatePayload }): Promise<string | null> => {
							if (data.target) {
								const path = await createNavigateTargetPath(data, {
									getContext: apiRef.current.getContext,
									getAppForgeUIExtensions,
								});
								if (!path) {
									return null;
								}

								// If the current domain is a custom domain, we need to request for the base url
								const baseUrl = isCustomDomain(window.location.hostname)
									? await getBaseUrl()
									: window.location.origin;

								// `getUrl` from `@forge/bridge` will return a URL object.
								// We are returning a string here as a URL object will serialised into a string when being passed across the bridge
								return `${baseUrl}${path}`;
							}
							return null;
						},
						navigate: withFausMessageForwarder(
							'navigate',
							async ({ data }: { data: NavigatePayload | TargetNavigatePayload }) => {
								// If a NavigationLocation object was provided:
								if ('target' in data && data.target) {
									const path = await createNavigateTargetPath(data, {
										getContext: apiRef.current.getContext,
										getAppForgeUIExtensions,
									});

									if (!path) {
										// eslint-disable-next-line no-console
										console.warn('Path was not found for the parameters provided');
										return;
									}

									// Request for the base url if the current domain is a custom domain
									if (isCustomDomain(window.location.hostname)) {
										const baseUrl = await getBaseUrl();
										return apiRef.current.onNavigate({
											type: data.type,
											url: `${baseUrl}${path}`,
										});
									} else {
										return apiRef.current.onNavigate({
											type: data.type,
											url: path,
										});
									}
								}
								return apiRef.current.onNavigate(data as NavigatePayload);
							},
						),
						reconcile: async ({ data }) => {
							return apiRef.current.reconcile(data);
						},
						onError: async ({ data }) => {
							return apiRef.current.onError(data);
						},
						reload: withFausMessageForwarder('reload', async () => {
							reload();
						}),
						refresh: async () => {
							return apiRef.current.refresh();
						},
						// WARNING — BAD PAYLOAD SHAPE FOR V2 (do not replicate for new bridge methods)
						//
						// The handlers below (`submit`, `onClose`, `close`, `openModal`) incorrectly pass
						// the raw port-rpc event object `{ data: payload }` to `apiRef.current.*` instead
						// of the unwrapped `payload` directly. This means product-side `BridgeConfig`
						// callbacks receive `{ data: payload }` and must access `opts.data` to get the
						// actual value — a leaky abstraction.
						//
						// New bridge methods should follow the pattern used by `fetchProduct`/`fetchRemote`/`reconcile`
						// below: destructure `{ data }` from the port-rpc event and pass `data` directly
						// to `apiRef.current.*`. The v2 bridge path (`bridge-config-delegates.ts`) already
						// re-wraps these specific keys as `{ data: payload }` to maintain backward
						// compatibility with existing product callbacks.
						//
						// Do NOT copy this pattern for new bridge methods.
						submit: async (payload?: any) => {
							return apiRef.current.submit(payload);
						},
						open: withFausMessageForwarder('open', async () => {
							return apiRef.current.open();
						}),
						onClose: async (payload?: any) => {
							return apiRef.current.onClose(payload);
						},
						close: async (payload?: any) => {
							return apiRef.current.close(payload);
						},
						emitReadyEvent: withFausMessageForwarder('emitReadyEvent', async () => {
							captureEvent({
								type: 'emit-ready-event',
								elapsed: getPerformanceMark(),
							});
							return apiRef.current.emitReadyEvent();
						}),

						getContext: () => {
							return apiRef.current.getContext();
						},
						openModal: withFausMessageForwarder('openModal', async (payload: any) => {
							return apiRef.current.openModal(payload);
						}),
						createHistory: async () => {
							return apiRef.current.createHistory();
						},
						fetchProduct: async ({ data }): Promise<FetchProductResponse> => {
							return apiRef.current.fetchProduct(data);
						},
						fetchRemote: async ({ data }): Promise<any> => {
							return apiRef.current.fetchRemote(data);
						},
						showFlag: withFausMessageForwarder('showFlag', async ({ data }: { data: any }) => {
							const flagActions = data.actions?.map((action: FlagAction) => {
								const { onClick } = action;
								return {
									...action,
									onClick: onClick ? () => onClick() : () => {},
								};
							});

							return apiRef.current.showFlag({
								...data,
								actions: flagActions,
							});
						}),
						closeFlag: withFausMessageForwarder('closeFlag', async ({ data }: { data: any }) =>
							apiRef.current.closeFlag(data),
						),
						on: async (payload: {
							data: {
								callback: (payload?: any) => Promise<any>;
								event: string;
							};
						}) => {
							const { event, callback } = payload.data;
							const subscription = on(event, callback, extensionDetails);
							eventCallbacksRef.current.push(subscription);
							return {
								unsubscribe: () => {
									eventCallbacksRef.current.splice(
										eventCallbacksRef.current.indexOf(subscription),
										1,
									);
									subscription.unsubscribe();
								},
							};
						},
						onPublic: async (payload: {
							data: {
								callback: (payload?: any) => Promise<any>;
								event: string;
							};
						}) => {
							const { event, callback } = payload.data;
							const subscription = onPublic(event, callback, extensionDetails);
							eventCallbacksRef.current.push(subscription);
							return {
								unsubscribe: () => {
									eventCallbacksRef.current.splice(
										eventCallbacksRef.current.indexOf(subscription),
										1,
									);
									subscription.unsubscribe();
								},
							};
						},
						emitPublic: async (payload: { data: { event: string; payload?: any } }) => {
							return apiRef.current.emitPublic?.(payload.data);
						},
						emit: withFausMessageForwarder(
							'emit',
							async (payload: {
								data: {
									embeddedMacroExtensionDetails?: ExtensionDetails;
									event: string;
									payload?: any;
								};
							}) => {
								return emit(
									payload.data.event,
									payload.data.payload,
									payload.data.embeddedMacroExtensionDetails || extensionDetails,
								);
							},
						),
						enableTheming: async (): Promise<void> => {
							await enableThemingRef.current?.();
							const currentTheme = getGlobalTheme();
							const surfaceColor = null;
							setThemeRef.current?.(currentTheme, surfaceColor, environment);
						},
						changeWindowTitle: async ({ data }) => {
							apiRef.current.changeWindowTitle(data);
						},
						getFrameId: async () => {
							return apiRef.current.getFrameId();
						},
						getFrameDispatch: async () => {
							return apiRef.current.getFrameDispatch();
						},
						subscribeRealtimeChannel: async (payload) => {
							return apiRef.current.subscribeRealtimeChannel?.(payload);
						},
						publishRealtimeChannel: async (payload) => {
							return apiRef.current.publishRealtimeChannel?.(payload);
						},
						openRovo: withFausMessageForwarder(
							'openRovo',
							async ({ data }: { data: OpenRovoPayload }) => {
								return apiRef.current.openRovo?.(data);
							},
						),
						requestTeamworkGraph: async ({ data }: { data: RequestTeamworkGraphPayload }) => {
							return apiRef.current.requestTeamworkGraph?.(data);
						},
						fetchUserRecommendations: async ({
							data,
						}: {
							data: FetchUserRecommendationsPayload;
						}) => {
							return apiRef.current.fetchUserRecommendations?.(data);
						},
						isRovoEnabled: withFausMessageForwarder('isRovoEnabled', async (): Promise<boolean> => {
							if (!cloudId) {
								return false;
							}
							return getIsRovoEnabled({ cloudId, relayEnvironment });
						}),
						trackObjectStoreAction: async ({ data }) => {
							trackObjectStoreAction({
								appType,
								appId,
								action: data.action,
								forgeEnvironment: environmentType,
							});
						},
						...getUseBridgePermissionFunctions(apiRef),
						...mapCustomBridgeMethods({
							customBridgeMethods: customBridgeMethodsRef.current,
							isUIKitEmbeddedMacro: fg('platform_forge_ui_refactor_macro_parent_type')
								? embeddedMacroParentType === 'UIKit'
								: isEmbeddedMacro && !isRenderedInFAUS,
							embeddedMacroLocalId: localId,
							withFausMessageForwarder,
						}),
					},

					page,
					environment,
					forgeEnvironment: environmentType,
					errorExtensionDetails,
					createAnalyticsEvent,
					tracing,
				}),
				sendHandshakeMetric: sendHandshakeMetric(page),
				shouldReceiveEmbeddedMacroMessages: isBodiedMacro && isProductLevel,
			});

			if (isResizable) {
				const isInNonJSMResizableModal = isInModal && !type?.startsWith('jiraServiceManagement');
				const isEditing = extensionData?.isEditing ?? extensionPayload?.isEditing;
				const isInInlineMacro =
					type === MACRO_MODULE_NAME && isInlineMacroPresentation({ isEditing, layout });

				const isDashboardFilterDropdown =
					type === 'dashboards:filter' && extensionPayload?.filter !== undefined;

				iframeResizer(
					{
						heightCalculationMethod: 'bodyScroll',
						widthCalculationMethod: 'bodyScroll',
						scrolling: true,
						// @ts-ignore minHeight can be a string
						minHeight: height,
						sizeWidth: isInNonJSMResizableModal || isInInlineMacro || isDashboardFilterDropdown,
						initCallback: (iframe) => {
							iframe?.iFrameResizer?.resize();
						},
					},
					iframeEl,
				);
			}

			const { close, enableTheming, setTheme } = open();
			setThemeRef.current = setTheme;
			enableThemingRef.current = enableTheming;

			// this tracking is to identify re-initializations of the bridge, which should never happen (or we should try avoid)
			// during the lifecycle of a Forge app. re-initialise bridge could be expensive and error prone (losing existing event subscriptions, etc)
			trackBridgeReinited();

			return () => {
				close?.(); // close may be undefined here if createIframeBridge call fails
				iframeEl?.iFrameResizer && iframeEl?.iFrameResizer.removeListeners();
			};
		}
		// We intentionally add react-hooks/exhaustive-deps disable here, so that we don't blindly
		// add new dependencies without considering the impact of re-creating the bridge.
		// The following dependencies are excluded for the reasons stated:
		//   - trackBridgeReinited - it's memoised and used for observability and should not trigger bridge recreation
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		getBaseUrl,
		iframeRef,
		origin,
		loading,
		page,
		isResizable,
		height,
		environment,
		errorExtensionDetails,
		id,
		layout,
		type,
		environmentType,
		installationId,
		tracing,
		createAnalyticsEvent,
		loadedAt,
		getAppForgeUIExtensions,
		appType,
		captureEvent,
		captureUntrustedEvent,
		appId,
		trackObjectStoreAction,
		isInModal,
		localId,
		isEmbeddedMacro,
		embeddedMacroParentType,
		embeddedParentLocalId,
	]);
	// [WARN] Please take careful consideration when adding new dependencies to the above useEffect.
	// createIframeBridge is expensive to call and re-creating the bridge could lead to lost event subscriptions.
	// - Try to avoid adding dependencies that could change on every render (e.g. objects or functions defined inline).
	// - If you need to add a function, consider wrapping it in useCallbackOne / useRef / useMemoOne to stabilise the reference.
	// - Please ensure multiple revewers in the Forge UI team review and approve the change.
	// - As the standard lint for dependency array is disabled, please ensure the dependency array is kept up to date with necessary dependencies.

	return {
		loading,
		iframeProps: {
			ref: iframeRef,
			onLoad: () => {
				onLoad && onLoad();
				setLoading(false);
				setLoadedAt(Date.now());
			},
		},
	};
};
